import socket from './socket'
import { UserAccessState } from '@caudena/core/states/UserAcessState'
import { PhoenixChannel } from './WebSocketChannel'
import {
  INotificationService,
  IPhoenixSocketController,
  ISentryService,
  WSStateInit,
} from './WebSocket.types'
import {
  notificationConfig,
  notificationToastOptions,
  REJECT_REASON,
} from '@caudena/core/constants'
import { inject, injectable, optional } from 'inversify'
import { DI_TYPES } from '../DI/types'

const TIMEOUT = 60_000

@injectable()
export class PhoenixSocketController implements IPhoenixSocketController {
  public phoenixSocket: typeof socket
  public channels: Map<string, PhoenixChannel> = new Map()
  public userState: UserAccessState
  public refreshToken: () => Promise<string | undefined>
  private WSChannel = PhoenixChannel
  private reconnectAttempts: number = 0
  private readonly MAX_RECONNECT_ATTEMPTS = 3
  private isRefreshingToken: boolean = false

  constructor(
    @inject(DI_TYPES.NotificationService)
    @optional()
    private notificationService: INotificationService,
    @inject(DI_TYPES.SentryService)
    @optional()
    private sentryService: ISentryService
  ) {}

  public init({ wss, token, userState, refreshToken }: WSStateInit) {
    this.reconnectAttempts = 0
    this.connect(wss, token)
    this.userState = userState
    this.refreshToken = refreshToken
  }

  public reconnectWithNewToken = (
    token: string,
    logout: () => void
  ): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      try {
        if (this.phoenixSocket) {
          console.log('WebSocket disconnecting for token refresh')
          this.phoenixSocket.disconnect(() => {
            if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
              console.log('Max reconnection attempts reached, logging out')
              logout()
              return reject(new Error('Max reconnection attempts reached'))
            }

            this.reconnectAttempts++
            console.log(
              `Reconnection attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS}`
            )

            // Listen for the connection open event
            const ref = this.phoenixSocket.onOpen(async () => {
              console.log('WebSocket reconnected, resetting reconnectAttempts')
              this.reconnectAttempts = 0
              // Remove the listener using the ref to ensure it doesn't fire again.
              this.phoenixSocket.off([ref])
              // Reset internal state, phoenix client will automatically rejoin
              this.channels.forEach((ch) => ch.reset())
              resolve()
            })

            // Trigger reconnection with the new token
            this.phoenixSocket.reconnect({ token })
          })
        } else {
          resolve()
        }
      } catch (error) {
        console.error('WebSocket reconnection error:', error)
        reject(error)
      }
    })
  }

  public channel(
    topic: string,
    params?: { [key: string]: unknown }
  ): PhoenixChannel {
    if (!this.channels.has(topic)) {
      this.channels.set(topic, this.createNewChannel(topic, params))
    }

    return this.channels.get(topic)!
  }

  public clear(topic: string) {
    if (this.channels.has(topic)) {
      const channel = this.channel(topic)

      channel.leave()
    }

    this.channels.delete(topic)
  }

  private connect(wss: string, token: string) {
    this.phoenixSocket = socket

    // Check if we're in a Node.js environment
    const isNodeEnv =
      typeof window === 'undefined' && typeof global !== 'undefined'

    try {
      // Initialize the socket differently based on environment
      if (isNodeEnv && global.WebSocket) {
        console.log(
          'Using WebSocket polyfill for Node.js environment with explicit transport'
        )
        // For Node.js environment with WebSocket polyfill
        const options = {
          params: { token },
          timeout: TIMEOUT,
          transport: global.WebSocket,
        }
        this.phoenixSocket.init(wss, options)
      } else {
        // For browser environment
        this.phoenixSocket.init(wss, { params: { token }, timeout: TIMEOUT })
      }

      // Only set onError if phoenix socket is initialized
      if (this.phoenixSocket) {
        this.phoenixSocket.onError((error) => {
          console.log('error socket', error)
        })
      }
    } catch (error) {
      console.error('Failed to initialize socket connection:', error)
    }
  }

  private errorHandled: { [key: string]: boolean } = {}

  private handleErrorOncePerTopic(topic: string, reason: string) {
    if (!this.errorHandled[topic]) {
      this.errorHandled[topic] = true

      this.sentryService?.captureException(new Error(reason), {
        extra: { topic },
      })
      this.notificationService?.notify(
        notificationConfig[REJECT_REASON.phx_error].text,
        { type: notificationConfig[REJECT_REASON.phx_error].type },
        notificationToastOptions
      )
    }
  }

  private createNewChannel(
    topic: string,
    params?: { [key: string]: unknown }
  ): PhoenixChannel {
    const errorHandler = (reason) => {
      if (reason?.reason?.toLowerCase() === 'subscription expired') {
        this.userState.setSubscriptionExpired(true)
      }

      if (
        ['no access', 'no subscription'].includes(
          reason?.reason?.toLowerCase() || ''
        )
      ) {
        this.userState.setNoAccess(true)
      }

      const reasonText =
        reason?.reason?.toLowerCase() ||
        reason?.reason?.error?.toLowerCase() ||
        ''

      if (
        [
          'token is invalid',
          'token is expired',
          'token expired',
          'token invalid',
        ].includes(reasonText)
      ) {
        // Предотвращаем параллельные refresh запросы из WebSocket
        if (!this.isRefreshingToken) {
          this.isRefreshingToken = true
          console.log('WebSocket: Token expired, triggering refresh')

          this.refreshToken()
            .then((newToken) => {
              if (newToken) {
                console.log('WebSocket: Token refreshed successfully')
              } else {
                console.log('WebSocket: Token refresh returned null')
              }
            })
            .catch((err) => {
              console.error('WebSocket: Token refresh failed:', err)
            })
            .finally(() => {
              this.isRefreshingToken = false
            })
        } else {
          console.log('WebSocket: Token refresh already in progress, skipping')
        }
        return reason
      }

      if (reason?.reason?.toLowerCase() === 'join crashed') {
        this.handleErrorOncePerTopic(topic, reason.reason)
      }
      return reason
    }
    return new this.WSChannel(
      topic,
      this.phoenixSocket?.channel?.(topic, params),
      errorHandler,
      this.notificationService,
      this.sentryService
    )
  }
}
